/**
 * Tuning history tracking.
 *
 * Every time a user marks a tuning session "complete" via Final Report, we
 * snapshot the key measurements into a history log per Setup. Later sessions
 * compare against history to surface drift trends.
 *
 * "Your brace height has dropped 1/16" in 3 weeks — string creep" is the
 * killer insight nobody else in the archery tool space provides.
 */

const HISTORY_STORE_KEY = 'sparrow_tuning_history';

export interface TuningSnapshot {
  /** ISO timestamp this snapshot was taken. */
  takenAt: string;
  /** Setup ID this snapshot belongs to. */
  setupId: string;
  /** Bow specs at time of snapshot (for traceability). */
  bow?: { brand?: string; model?: string; year?: number };
  /** The measurements that matter for drift detection. */
  measurements: {
    measuredDrawWeight?: number;
    measuredDrawLength?: number;
    measuredAxleToAxle?: number;
    measuredBraceHeight?: number;
    tillerTopMinusBottom?: number;
    centerShotInches?: number;
    nockHeightAboveSquare?: number;
  };
  /** User's free-text notes from this session, surfaced in the trend chart. */
  notes?: string;
}

export interface DriftAlert {
  metric: string;
  delta: number;
  units: string;
  /** "increased" / "decreased". */
  direction: 'increased' | 'decreased';
  /** Days between the two snapshots being compared. */
  daysBetween: number;
  /** Likely cause hint based on archery-domain heuristics. */
  hint: string;
}

/**
 * Append a new snapshot to the history log. Capped at 50 snapshots per setup
 * (oldest evicted) so localStorage doesn't bloat over years of tuning.
 */
export function pushSnapshot(snap: TuningSnapshot): void {
  const all = loadAll();
  const forSetup = all[snap.setupId] ?? [];
  forSetup.push(snap);
  // Keep last 50
  while (forSetup.length > 50) forSetup.shift();
  all[snap.setupId] = forSetup;
  persistAll(all);
}

/** All snapshots for a setup, oldest first. */
export function getHistory(setupId: string): TuningSnapshot[] {
  const all = loadAll();
  return all[setupId] ?? [];
}

/** Wipe history for one setup (e.g. user re-strung the bow and wants fresh baseline). */
export function clearHistory(setupId: string): void {
  const all = loadAll();
  delete all[setupId];
  persistAll(all);
}

/**
 * Detect drift between the most recent snapshot and the previous one.
 * Returns alerts for any measurement that's moved outside normal tolerance.
 *
 * Tolerance thresholds (industry standard):
 *  - brace height: ±1/32" considered drift (0.031")
 *  - draw weight: ±2 lb considered drift
 *  - draw length: ±0.25" considered drift
 *  - tiller: ±1/16" considered drift
 *  - center shot: ±1/64" considered drift
 *  - nock height: ±1/64" considered drift
 */
export function detectDrift(setupId: string): DriftAlert[] {
  const hist = getHistory(setupId);
  if (hist.length < 2) return [];
  const latest = hist[hist.length - 1]!;
  const prior = hist[hist.length - 2]!;
  const daysBetween = Math.round(
    (new Date(latest.takenAt).getTime() - new Date(prior.takenAt).getTime()) / (1000 * 60 * 60 * 24),
  );

  const alerts: DriftAlert[] = [];

  const check = (
    key: keyof TuningSnapshot['measurements'],
    label: string,
    tolerance: number,
    units: string,
    hint: string,
  ) => {
    const newVal = latest.measurements[key];
    const oldVal = prior.measurements[key];
    if (newVal === undefined || oldVal === undefined) return;
    const delta = newVal - oldVal;
    if (Math.abs(delta) <= tolerance) return;
    alerts.push({
      metric: label,
      delta: Math.abs(delta),
      units,
      direction: delta > 0 ? 'increased' : 'decreased',
      daysBetween,
      hint,
    });
  };

  check('measuredBraceHeight', 'Brace height', 0.0625, '"',
    'Brace shifts ~3 fps per 1/8". Strings stretching is the usual cause — re-time cams + retape.');
  check('measuredDrawWeight', 'Draw weight', 2, ' lb',
    'Limb bolts may have backed off or string set has shifted. Re-check tiller after fixing.');
  check('measuredDrawLength', 'Draw length', 0.25, '"',
    'Cable creep can shift draw length. Module set screw or twist a buss cable to bring back.');
  check('measuredAxleToAxle', 'Axle-to-axle', 0.125, '"',
    'ATA growth = cable stretch. Time to consider replacement strings if > 1/4" off spec.');
  check('tillerTopMinusBottom', 'Tiller', 0.0625, '"',
    'Uneven cable creep. Adjust limb bolts (1/2 turn ≈ 1/16" tiller).');
  check('centerShotInches', 'Center shot', 0.03125, '"',
    'Rest may have shifted from impact or vibration. Check rest mount torque.');
  check('nockHeightAboveSquare', 'Nock height', 0.03125, '"',
    'D-loop may be slipping along the string. Re-tie tighter or replace D-loop material.');

  return alerts;
}

// ─────────────────────────────────────────────────────────────────────
// Storage layer
// ─────────────────────────────────────────────────────────────────────

function loadAll(): Record<string, TuningSnapshot[]> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(HISTORY_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, TuningSnapshot[]>;
  } catch {
    return {};
  }
}

function persistAll(history: Record<string, TuningSnapshot[]>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HISTORY_STORE_KEY, JSON.stringify(history));
  } catch {
    // sandboxed iframe or quota — silently degrade
  }
}
